import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import { Printer, Search, Zap } from 'lucide-react';
import inventoryService, { Station } from '../../services/inventoryService';
import locationService, { Location } from '../../services/locationService';
import stationManagementService from '../../services/stationManagementService';
import { useLanguage } from '../../i18n/LanguageContext';

interface StationFormModalProps {
  station: Station | null;
  onClose: () => void;
  onSuccess: () => void;
}

const StationFormModal: React.FC<StationFormModalProps> = ({ station, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    station_name: '',
    warehouse_code: '',
    location_code: '',
    station_type: 'production',
    assigned_product_sku: '',
    assigned_session_number: '',
  });

  const [printerMac, setPrinterMac] = useState('');
  const [discoveredPrinters, setDiscoveredPrinters] = useState<Array<{ ip: string; mac: string; reachable: boolean }>>([]);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch locations from location service
  const { data: locationsResponse } = useQuery('locations-for-stations', () =>
    locationService.getLocations({ active: true, limit: 1000 })
  );

  const locations = locationsResponse?.data?.locations || [];

  // Fetch warehouses from location service
  const { data: warehouses } = useQuery('warehouses-for-stations', () =>
    locationService.getWarehouses()
  );

  // Fetch available production sessions
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
  const { data: productionSessions } = useQuery(['production-sessions-available'], async () => {
    const response = await fetch(`${apiBaseUrl}/api/inventory/production-sessions/available`);
    const result = await response.json();
    return result.data || [];
  });

  // Create mutation
  const createMutation = useMutation(
    (data: typeof formData) => inventoryService.createStation(data),
    {
      onSuccess: () => {
        toast.success('Station created successfully');
        onSuccess();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to create station');
      },
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    (data: typeof formData) => inventoryService.updateStation(station!.station_id, data),
    {
      onSuccess: () => {
        toast.success('Station updated successfully');
        onSuccess();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update station');
      },
    }
  );

  // Populate form when editing — also load printer MAC from station-service
  useEffect(() => {
    if (station) {
      setFormData({
        station_name: station.station_name,
        warehouse_code: station.warehouse_code,
        location_code: station.location_code,
        station_type: station.station_type || 'production',
        assigned_product_sku: station.assigned_product_sku || '',
        assigned_session_number: '',
      });
      // Load printer MAC for this station
      stationManagementService.getStationPrinters().then(stations => {
        const match = stations.find(s => s.station_id === station.station_id);
        if (match?.printer_mac_id) setPrinterMac(match.printer_mac_id);
      }).catch((err) => {
        console.warn('Failed to load printer MAC for station', station.station_id, err);
        toast.error(t('messages.loadFailed').replace('{item}', 'printer config'));
      });
    }
  }, [station]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.station_name.trim()) {
      newErrors.station_name = 'Station name is required';
    }

    if (!formData.warehouse_code.trim()) {
      newErrors.warehouse_code = 'Warehouse code is required';
    }

    if (!formData.location_code) {
      newErrors.location_code = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Validate MAC format if provided
    if (printerMac && !/^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(printerMac)) {
      setErrors(prev => ({ ...prev, printer_mac_id: 'Invalid MAC format. Use XX:XX:XX:XX:XX:XX' }));
      return;
    }

    if (station) {
      updateMutation.mutate(formData, {
        onSuccess: () => {
          // Save printer MAC after station update
          stationManagementService.updateStationPrinter(station.station_id, printerMac || null).catch(() => {
            toast.error(t('stations.printerLinkError'));
          });
        }
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Discover printers on the network
  const handleDiscover = async () => {
    setIsDiscovering(true);
    try {
      const result = await stationManagementService.discoverPrinters();
      setDiscoveredPrinters(result.printers || []);
      if (!result.printers?.length) {
        toast.error(t('stations.noPrintersFound'));
      }
    } catch {
      toast.error(t('stations.noPrintersFound'));
    } finally {
      setIsDiscovering(false);
    }
  };

  // Test print to station's assigned printer
  const handleTestPrint = async () => {
    if (!station) return;
    setIsTesting(true);
    try {
      const result = await stationManagementService.testStationPrinter(station.station_id);
      if (result.success) {
        toast.success(t('stations.testPrintSuccess'));
      } else {
        toast.error(result.error || t('stations.testPrintFailed'));
      }
    } catch {
      toast.error(t('stations.testPrintFailed'));
    } finally {
      setIsTesting(false);
    }
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Modal Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {station ? 'Edit Station' : 'Add New Station'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {station ? 'Update station details and configuration' : 'Create a new production station'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Station Name */}
            <div>
              <label htmlFor="station_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Station Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="station_name"
                name="station_name"
                value={formData.station_name}
                onChange={handleChange}
                placeholder="e.g., Packing Station-5"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  errors.station_name ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.station_name && (
                <p className="mt-1 text-sm text-red-500">{errors.station_name}</p>
              )}
            </div>

            {/* Location */}
            <div>
              <label htmlFor="location_code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Production Location <span className="text-red-500">*</span>
              </label>
              <select
                id="location_code"
                name="location_code"
                value={formData.location_code}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  errors.location_code ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              >
                <option value="">Select Location</option>
                {locations?.map((loc: Location) => (
                  <option key={loc.locationCode} value={loc.locationCode}>
                    {loc.description} ({loc.locationCode})
                  </option>
                ))}
              </select>
              {errors.location_code && (
                <p className="mt-1 text-sm text-red-500">{errors.location_code}</p>
              )}
            </div>

            {/* Warehouse Code */}
            <div>
              <label htmlFor="warehouse_code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Warehouse Code <span className="text-red-500">*</span>
              </label>
              <select
                id="warehouse_code"
                name="warehouse_code"
                value={formData.warehouse_code}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  errors.warehouse_code ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              >
                <option value="">Select Warehouse</option>
                {warehouses?.map((warehouse) => (
                  <option key={warehouse.warehouseCode} value={warehouse.warehouseCode}>
                    {warehouse.warehouseCode} - {warehouse.description}
                  </option>
                ))}
              </select>
              {errors.warehouse_code && (
                <p className="mt-1 text-sm text-red-500">{errors.warehouse_code}</p>
              )}
            </div>

            {/* Station Type */}
            <div>
              <label htmlFor="station_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Station Type
              </label>
              <select
                id="station_type"
                name="station_type"
                value={formData.station_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
              >
                <option value="production">Production</option>
                <option value="packing">Packing</option>
                <option value="assembly">Assembly</option>
                <option value="quality_control">Quality Control</option>
                <option value="finishing">Finishing</option>
              </select>
            </div>

            {/* Assigned Product */}
            <div>
              <label htmlFor="assigned_session_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Assigned Product for Production Runs
              </label>
              <select
                id="assigned_session_number"
                name="assigned_session_number"
                value={formData.assigned_session_number}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isLoading}
              >
                <option value="">No production session assigned</option>
                {productionSessions?.map((session: any) => (
                  <option key={session.session_number} value={session.session_number}>
                    {session.session_number} - {session.product_name} ({session.product_sku})
                  </option>
                ))}
              </select>
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex gap-2">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-blue-800">
                    <div className="font-medium mb-1">Production Assignment</div>
                    <div>
                      Assign a primary product to this station for production runs. 
                      Operators will see this product when starting a session. 
                      You can also use the "Assign Product" quick action for faster assignment.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Printer Assignment */}
            {station && (
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Printer className="w-4 h-4 text-gray-500" />
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('stations.printerMac')}
                  </label>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={printerMac}
                    onChange={(e) => {
                      setPrinterMac(e.target.value.toUpperCase());
                      if (errors.printer_mac_id) setErrors(prev => ({ ...prev, printer_mac_id: '' }));
                    }}
                    placeholder={t('stations.printerMacPlaceholder')}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm ${
                      errors.printer_mac_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isLoading}
                    maxLength={17}
                  />
                  <button
                    type="button"
                    onClick={handleDiscover}
                    disabled={isDiscovering || isLoading}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm flex items-center gap-1"
                    title={t('stations.discoverPrinters')}
                  >
                    <Search className="w-4 h-4" />
                    {isDiscovering ? t('stations.discovering') : t('stations.discoverPrinters')}
                  </button>
                  {printerMac && (
                    <button
                      type="button"
                      onClick={handleTestPrint}
                      disabled={isTesting || isLoading}
                      className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm flex items-center gap-1"
                      title={t('stations.testPrint')}
                    >
                      <Zap className="w-4 h-4" />
                      {isTesting ? '...' : t('stations.testPrint')}
                    </button>
                  )}
                </div>
                {errors.printer_mac_id && (
                  <p className="mt-1 text-sm text-red-500">{errors.printer_mac_id}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('stations.printerMacDesc')}</p>

                {/* Discovered Printers List */}
                {discoveredPrinters.length > 0 && (
                  <div className="mt-3 border rounded-md divide-y max-h-40 overflow-y-auto">
                    {discoveredPrinters.map((printer) => (
                      <button
                        key={printer.mac}
                        type="button"
                        onClick={() => {
                          setPrinterMac(printer.mac.toUpperCase());
                          setDiscoveredPrinters([]);
                          toast.success(t('stations.printerAssigned'));
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 dark:hover:bg-gray-700 flex justify-between items-center"
                      >
                        <span className="font-mono">{printer.mac.toUpperCase()}</span>
                        <span className="text-xs text-gray-500">{printer.ip}</span>
                      </button>
                    ))}
                  </div>
                )}

                {printerMac && (
                  <button
                    type="button"
                    onClick={() => setPrinterMac('')}
                    className="mt-2 text-xs text-red-500 hover:text-red-700"
                  >
                    {t('stations.removePrinter')}
                  </button>
                )}
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : station ? 'Update Station' : 'Create Station'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StationFormModal;
